import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const GOAL_TYPES = [
  {
    value: 'steps',
    label: 'Steps',
    unit: 'steps',
    icon: 'Footprints',
    color: 'primary',
    description: 'Daily step count goal',
    defaultTarget: 10000,
    placeholder: '10000',
  },
  {
    value: 'calories',
    label: 'Calories',
    unit: 'kcal',
    icon: 'Flame',
    color: 'warning',
    description: 'Calories burned goal',
    defaultTarget: 500,
    placeholder: '500',
  },
  {
    value: 'sleep',
    label: 'Sleep',
    unit: 'hours',
    icon: 'Moon',
    color: 'accent',
    description: 'Nightly sleep hours goal',
    defaultTarget: 8,
    placeholder: '8',
  },
  {
    value: 'hydration',
    label: 'Hydration',
    unit: 'glasses',
    icon: 'Droplets',
    color: 'success',
    description: 'Daily water intake goal',
    defaultTarget: 8,
    placeholder: '8',
  },
  {
    value: 'workouts',
    label: 'Workouts',
    unit: 'sessions',
    icon: 'Dumbbell',
    color: 'error',
    description: 'Workout sessions goal',
    defaultTarget: 5,
    placeholder: '5',
  },
];

const DURATION_PRESETS = [
  { label: '3 days', value: 3 },
  { label: '1 week', value: 7 },
  { label: '2 weeks', value: 14 },
  { label: '1 month', value: 30 },
];

const SCORING_RULES = [
  {
    value: 'cumulative',
    label: 'Cumulative Total',
    icon: 'TrendingUp',
    description: 'Sum all progress over the challenge period',
  },
  {
    value: 'daily_best',
    label: 'Daily Best',
    icon: 'Star',
    description: 'Best single-day performance wins',
  },
  {
    value: 'streak',
    label: 'Streak',
    icon: 'Zap',
    description: 'Longest consecutive days hitting the goal',
  },
  {
    value: 'first_to_complete',
    label: 'First to Complete',
    icon: 'Trophy',
    description: 'First member to reach the target wins',
  },
];

const colorMap = {
  primary: { bg: 'bg-primary/10', border: 'border-primary', text: 'text-primary', icon: 'bg-primary' },
  warning: { bg: 'bg-warning/10', border: 'border-warning', text: 'text-warning', icon: 'bg-warning' },
  accent: { bg: 'bg-accent/10', border: 'border-accent', text: 'text-accent', icon: 'bg-accent' },
  success: { bg: 'bg-success/10', border: 'border-success', text: 'text-success', icon: 'bg-success' },
  error: { bg: 'bg-error/10', border: 'border-error', text: 'text-error', icon: 'bg-error' },
};

const LaunchChallengeModal = ({ isOpen, onClose, squadId, userId, squadName, onLaunched }) => {
  const [step, setStep] = useState(1); // 1: goal type, 2: details, 3: scoring & team
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    title: '',
    description: '',
    goalType: 'steps',
    targetValue: '',
    durationDays: 7,
    customDuration: '',
    useCustomDuration: false,
    scoringRule: 'cumulative',
    teamEntry: false,
    notes: '',
  });

  const selectedGoal = GOAL_TYPES?.find(g => g?.value === form?.goalType);
  const selectedColors = colorMap?.[selectedGoal?.color] || colorMap?.primary;

  const handleGoalSelect = (goalType) => {
    const goal = GOAL_TYPES?.find(g => g?.value === goalType);
    setForm(prev => ({
      ...prev,
      goalType,
      targetValue: String(goal?.defaultTarget || ''),
    }));
  };

  const handleDurationPreset = (days) => {
    setForm(prev => ({ ...prev, durationDays: days, useCustomDuration: false, customDuration: '' }));
  };

  const handleCustomDuration = (val) => {
    const parsed = parseInt(val) || '';
    setForm(prev => ({
      ...prev,
      customDuration: val,
      durationDays: parsed || prev?.durationDays,
      useCustomDuration: true,
    }));
  };

  const effectiveDuration = form?.useCustomDuration
    ? parseInt(form?.customDuration) || form?.durationDays
    : form?.durationDays;

  const canProceedStep1 = !!form?.goalType;
  const canProceedStep2 = form?.title?.trim()?.length > 0 && parseFloat(form?.targetValue) > 0 && effectiveDuration > 0;
  const canSubmit = canProceedStep2 && !!form?.scoringRule;

  const handleLaunch = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate?.setDate(endDate?.getDate() + effectiveDuration);

      // 1. Create the wellness challenge
      const { data: challenge, error: challengeErr } = await supabase
        ?.from('wellness_challenges')
        ?.insert({
          title: form?.title?.trim(),
          description: form?.description?.trim() || null,
          challenge_type: form?.goalType,
          target_value: parseFloat(form?.targetValue),
          unit: selectedGoal?.unit,
          start_date: startDate?.toISOString()?.split('T')?.[0],
          end_date: endDate?.toISOString()?.split('T')?.[0],
          icon: selectedGoal?.icon,
          color: selectedGoal?.color,
          is_active: true,
        })
        ?.select()
        ?.single();

      if (challengeErr) throw challengeErr;

      // 2. Link challenge to squad
      const { error: squadChallengeErr } = await supabase
        ?.from('squad_challenges')
        ?.insert({
          squad_id: squadId,
          challenge_id: challenge?.id,
          created_by: userId,
          scoring_rule: form?.scoringRule,
          team_entry: form?.teamEntry,
          notes: form?.notes?.trim() || null,
        });

      if (squadChallengeErr) throw squadChallengeErr;

      // 3. Auto-enroll creator as participant
      await supabase
        ?.from('challenge_participants')
        ?.insert({
          challenge_id: challenge?.id,
          user_id: userId,
          current_progress: 0,
          is_completed: false,
        })
        ?.select();

      onLaunched?.(challenge);
      handleClose();
    } catch (err) {
      console.error('Error launching challenge:', err);
      setError(err?.message || 'Failed to launch challenge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setError(null);
    setForm({
      title: '',
      description: '',
      goalType: 'steps',
      targetValue: '10000',
      durationDays: 7,
      customDuration: '',
      useCustomDuration: false,
      scoringRule: 'cumulative',
      teamEntry: false,
      notes: '',
    });
    onClose?.();
  };

  if (!isOpen) return null;

  const stepLabels = ['Goal Type', 'Details', 'Rules & Team'];

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        className="relative w-full max-w-2xl bg-card rounded-2xl shadow-2xl border border-border max-h-[92vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border/60 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${selectedColors?.icon} flex items-center justify-center`}>
              <Icon name="Rocket" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Launch Squad Challenge</h2>
              <p className="text-xs text-muted-foreground">{squadName || 'Your Squad'}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-3 flex items-center gap-2 flex-shrink-0">
          {stepLabels?.map((label, idx) => {
            const stepNum = idx + 1;
            const isActive = step === stepNum;
            const isDone = step > stepNum;
            return (
              <React.Fragment key={label}>
                <div className="flex items-center gap-1.5">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      isDone
                        ? 'bg-success text-white'
                        : isActive
                        ? `${selectedColors?.icon} text-white`
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {isDone ? <Icon name="Check" size={12} /> : stepNum}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${
                    isActive ? 'text-foreground' : 'text-muted-foreground'
                  }`}>{label}</span>
                </div>
                {idx < stepLabels?.length - 1 && (
                  <div className={`flex-1 h-0.5 rounded-full transition-all ${
                    step > stepNum ? 'bg-success' : 'bg-muted'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <AnimatePresence mode="wait">
            {/* ── Step 1: Goal Type ── */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="py-4 space-y-4"
              >
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">Select a Goal Type</p>
                  <p className="text-xs text-muted-foreground mb-4">Choose what your squad will be competing on</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {GOAL_TYPES?.map(goal => {
                      const colors = colorMap?.[goal?.color];
                      const isSelected = form?.goalType === goal?.value;
                      return (
                        <button
                          key={goal?.value}
                          onClick={() => handleGoalSelect(goal?.value)}
                          className={`flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                            isSelected
                              ? `${colors?.bg} ${colors?.border}`
                              : 'border-border bg-muted/10 hover:border-border/80 hover:bg-muted/20'
                          }`}
                        >
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            isSelected ? colors?.icon : 'bg-muted'
                          }`}>
                            <Icon
                              name={goal?.icon}
                              size={22}
                              className={isSelected ? 'text-white' : 'text-muted-foreground'}
                            />
                          </div>
                          <div className="min-w-0">
                            <p className={`font-semibold text-sm ${
                              isSelected ? colors?.text : 'text-foreground'
                            }`}>{goal?.label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{goal?.description}</p>
                            <p className={`text-xs font-medium mt-1 ${
                              isSelected ? colors?.text : 'text-muted-foreground'
                            }`}>Unit: {goal?.unit}</p>
                          </div>
                          {isSelected && (
                            <div className={`ml-auto w-5 h-5 rounded-full ${colors?.icon} flex items-center justify-center flex-shrink-0`}>
                              <Icon name="Check" size={12} color="white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Step 2: Challenge Details ── */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="py-4 space-y-5"
              >
                {/* Goal type badge */}
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${selectedColors?.bg} border ${selectedColors?.border} w-fit`}>
                  <Icon name={selectedGoal?.icon} size={15} className={selectedColors?.text} />
                  <span className={`text-xs font-semibold ${selectedColors?.text}`}>{selectedGoal?.label} Challenge</span>
                </div>

                <Input
                  label="Challenge Title"
                  value={form?.title}
                  onChange={e => setForm(prev => ({ ...prev, title: e?.target?.value }))}
                  placeholder={`e.g. ${squadName || 'Squad'} ${selectedGoal?.label} Blitz`}
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
                  <textarea
                    value={form?.description}
                    onChange={e => setForm(prev => ({ ...prev, description: e?.target?.value }))}
                    placeholder="Describe the challenge goal and any special rules..."
                    rows={3}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all resize-none text-sm"
                  />
                </div>

                {/* Target Value */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Target Goal
                    <span className="ml-1 text-muted-foreground font-normal">({selectedGoal?.unit})</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      value={form?.targetValue}
                      onChange={e => setForm(prev => ({ ...prev, targetValue: e?.target?.value }))}
                      placeholder={selectedGoal?.placeholder}
                      className="w-full px-4 py-3 pr-20 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all text-sm"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-medium">
                      {selectedGoal?.unit}
                    </span>
                  </div>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">Duration</label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {DURATION_PRESETS?.map(preset => (
                      <button
                        key={preset?.value}
                        onClick={() => handleDurationPreset(preset?.value)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                          !form?.useCustomDuration && form?.durationDays === preset?.value
                            ? `${selectedColors?.bg} ${selectedColors?.border} ${selectedColors?.text}`
                            : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                        }`}
                      >
                        {preset?.label}
                      </button>
                    ))}
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={form?.customDuration}
                        onChange={e => handleCustomDuration(e?.target?.value)}
                        placeholder="Custom"
                        className={`w-24 px-3 py-2 rounded-lg text-sm border transition-all bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                          form?.useCustomDuration
                            ? `${selectedColors?.border} ${selectedColors?.bg}`
                            : 'border-border'
                        }`}
                      />
                      {form?.useCustomDuration && (
                        <span className="text-xs text-muted-foreground">days</span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Challenge runs for <span className="font-semibold text-foreground">{effectiveDuration} day{effectiveDuration !== 1 ? 's' : ''}</span>
                  </p>
                </div>
              </motion.div>
            )}

            {/* ── Step 3: Scoring Rules & Team Entry ── */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="py-4 space-y-6"
              >
                {/* Challenge Summary */}
                <div className={`p-4 rounded-xl border ${selectedColors?.border} ${selectedColors?.bg}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-9 h-9 rounded-lg ${selectedColors?.icon} flex items-center justify-center`}>
                      <Icon name={selectedGoal?.icon} size={18} color="white" />
                    </div>
                    <div>
                      <p className={`font-bold text-sm ${selectedColors?.text}`}>{form?.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {form?.targetValue} {selectedGoal?.unit} · {effectiveDuration} days
                      </p>
                    </div>
                  </div>
                </div>

                {/* Scoring Rules */}
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">Scoring Rule</p>
                  <p className="text-xs text-muted-foreground mb-3">How will winners be determined?</p>
                  <div className="space-y-2">
                    {SCORING_RULES?.map(rule => {
                      const isSelected = form?.scoringRule === rule?.value;
                      return (
                        <button
                          key={rule?.value}
                          onClick={() => setForm(prev => ({ ...prev, scoringRule: rule?.value }))}
                          className={`w-full flex items-center gap-4 p-3.5 rounded-xl border-2 text-left transition-all ${
                            isSelected
                              ? `${selectedColors?.bg} ${selectedColors?.border}`
                              : 'border-border hover:border-border/80 hover:bg-muted/10'
                          }`}
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isSelected ? selectedColors?.icon : 'bg-muted'
                          }`}>
                            <Icon
                              name={rule?.icon}
                              size={18}
                              className={isSelected ? 'text-white' : 'text-muted-foreground'}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold ${
                              isSelected ? selectedColors?.text : 'text-foreground'
                            }`}>{rule?.label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{rule?.description}</p>
                          </div>
                          {isSelected && (
                            <div className={`w-5 h-5 rounded-full ${selectedColors?.icon} flex items-center justify-center flex-shrink-0`}>
                              <Icon name="Check" size={11} color="white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Team Entry Toggle */}
                <div className="p-4 rounded-xl border border-border bg-muted/10">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name="Users" size={18} className="text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">Team Entry</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          All squad members are automatically enrolled when the challenge launches
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setForm(prev => ({ ...prev, teamEntry: !prev?.teamEntry }))}
                      className={`relative w-11 h-6 rounded-full transition-all flex-shrink-0 mt-0.5 ${
                        form?.teamEntry ? selectedColors?.icon : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
                          form?.teamEntry ? 'left-5' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </div>
                  {form?.teamEntry && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className={`mt-3 pt-3 border-t ${selectedColors?.border} flex items-center gap-2`}
                    >
                      <Icon name="Info" size={14} className={selectedColors?.text} />
                      <p className={`text-xs ${selectedColors?.text} font-medium`}>
                        All squad members will be enrolled automatically
                      </p>
                    </motion.div>
                  )}
                </div>

                {/* Admin Notes */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Admin Notes <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={form?.notes}
                    onChange={e => setForm(prev => ({ ...prev, notes: e?.target?.value }))}
                    placeholder="Any additional rules or notes for squad members..."
                    rows={2}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all resize-none text-sm"
                  />
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-error/10 border border-error/30">
                    <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-error">{error}</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border/60 flex items-center justify-between gap-3 flex-shrink-0 bg-card">
          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep(s => s - 1)}
                iconName="ChevronLeft"
                disabled={loading}
              >
                Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            {step < 3 ? (
              <Button
                size="sm"
                onClick={() => setStep(s => s + 1)}
                disabled={(step === 1 && !canProceedStep1) || (step === 2 && !canProceedStep2)}
                iconName="ChevronRight"
                className={selectedColors?.icon}
              >
                Next
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleLaunch}
                loading={loading}
                disabled={!canSubmit || loading}
                iconName="Rocket"
              >
                Launch Challenge
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default LaunchChallengeModal;
